import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import {
  QuillEditorComponent,
  type QuillModules,
  type SelectionChange,
} from 'ngx-quill';

export const WYSIWYG_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => WysiwygComponent),
  multi: true,
};

/**
 * The overloading of the styling in the Quill library is done globally in the file lib/scss/_wysiwyg.scss
 */
@Component({
  selector: 'myre-wysiwyg',
  templateUrl: './wysiwyg.component.html',
  styleUrls: ['./wysiwyg.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WYSIWYG_VALUE_ACCESSOR],
  imports: [QuillEditorComponent, FormsModule],
})
export class WysiwygComponent implements ControlValueAccessor {
  private _changeDetectionRef = inject(ChangeDetectorRef);

  @Input()
  public height: number = 120;

  @Input()
  public placeholder: string;

  public disabled: boolean = false;

  @Input()
  public readonly: boolean = false;

  @Input()
  public required: boolean = false;

  /**
   * The currently selected plain text in the editor. Emits the text on a real
   * selection, '' when a click collapses the selection while focused (a
   * deliberate deselect), and does NOT emit on blur — so a consumer can keep the
   * last selection while the user moves to another control to act on it.
   */
  @Output()
  public selectedTextChanged = new EventEmitter<string>();

  public quillConfig: QuillModules = {
    toolbar: {
      container: [
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
    },
    clipboard: {
      /**
       * Important: when upgrading the quill.js library to v2, this option will be removed.
       * See {@link https://github.com/quilljs/quill/blob/ee827ffb605ba491246f201d497ce0e7d9e193a0/docs/guides/upgrading-to-2-0.md}
       * We will need to use helper {@link trimQuillHtmlEmptyLines} instead.
       */
      matchVisual: false,
    },
  };

  public value: string;

  public registerOnChange(fn: (value: string) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public writeValue(value: string): void {
    this.value = value;
    this._changeDetectionRef.markForCheck();
  }

  public onChange(value: string): void {
    this.value = value;
    this._onModelChange(value);
  }

  public onBlur(): void {
    this._onTouched();
  }

  public onSelectionChanged(event: SelectionChange): void {
    const { editor, range } = event;

    // Blur (range === null): stay silent so a consumer can KEEP the last selection
    // while the user moves to another control to act on it (e.g. an AI-edit field).
    if (!range) {
      return;
    }

    // A collapsed range is a click that placed the caret while focused — the user
    // deselected on purpose, so emit '' to clear. A non-empty range is a real
    // selection and replaces it.
    this.selectedTextChanged.emit(
      range.length === 0
        ? ''
        : editor.getText(range.index, range.length).trim(),
    );
  }

  private _onModelChange: (value: string) => any = () => {};

  private _onTouched: () => any = () => {};
}
